import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { SkeletonModule } from 'primeng/skeleton';
import { type PaginatorState } from 'primeng/paginator';

import { AuthService } from '@/core/auth/auth.service';
import { CctService, CctDetalheResponse } from '@/core/api/sicom-api/gestao/cct/cct.service';
import { MedicoEstatisticaAgrupadaResponse } from '@/core/api/sicom-api/registro/medico/controle-administrativo/medico-estatistica-agrupada.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { MapaUfCard } from '@/shared/components/mapa-uf-card/mapa-uf-card';
import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { EstatisticaMensalUtils } from '@/shared/utils/estatistica-mensal-utils';
import { DetalhamentoPendencias } from '../detalhamento-pendencias/detalhamento-pendencias';
import { IndicadorPendencia } from '../mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../mapa-pendencias/pendencia-linha.model';
import { UfSelecaoGestaoService } from '../uf-selecao-gestao.service';

type CctVisualizacao = 'linha' | 'barra' | 'rosca';
type CctModoTotal = 'soma' | 'primeiro';

type CctCardId = 'comissoes-tipo' | 'comissoes-situacao' | 'comissoes-uf' | 'reunioes-mes' | 'reunioes-modalidade' | 'reunioes-status' | 'tarefas-situacao';

type CctCard = {
    id: CctCardId;
    titulo: string;
    descricao: string;
    classeGrupo: string;
    visualizacao: CctVisualizacao;
    rotuloTotal: string;
    modoTotal: CctModoTotal;
    linhaInteira?: boolean;
    itens: MedicoEstatisticaAgrupadaResponse[] | null;
    carregando: boolean;
};

const CCT_CARDS_MENSAIS: ReadonlySet<CctCardId> = new Set<CctCardId>(['reunioes-mes']);

@Component({
    selector: 'app-comissoes-tecnicas',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CardModule, ChartModule, SkeletonModule, CardContent, SelectEstado, MapaUfCard, PainelLateral, DetalhamentoPendencias],
    templateUrl: './comissoes-tecnicas.html',
    styleUrl: '../estatisticas-cadastro/estatisticas-cadastro.scss'
})
export class ComissoesTecnicas implements OnInit {
    readonly anoAtual = new Date().getFullYear();
    readonly limiteRanking = 10;

    anoSelecionado = this.anoAtual;
    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;

    cards: CctCard[] = [
        {
            id: 'comissoes-situacao',
            titulo: 'Comissões por situação',
            descricao: 'Comissões técnicas vigentes e extintas.',
            classeGrupo: 'grupo-situacao',
            visualizacao: 'rosca',
            rotuloTotal: 'comissões',
            modoTotal: 'soma',
            itens: null,
            carregando: false
        },
        {
            id: 'comissoes-tipo',
            titulo: 'Comissões vigentes por tipo',
            descricao: 'Ranking de tipos de comissão técnica vigente.',
            classeGrupo: 'grupo-perfil',
            visualizacao: 'barra',
            rotuloTotal: 'comissões',
            modoTotal: 'soma',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'comissoes-uf',
            titulo: 'Comissões vigentes por UF',
            descricao: 'Distribuição das comissões técnicas vigentes por UF.',
            classeGrupo: 'grupo-endereco',
            visualizacao: 'barra',
            rotuloTotal: 'comissões',
            modoTotal: 'soma',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'reunioes-mes',
            titulo: 'Reuniões por mês',
            descricao: 'Volume mensal de reuniões no ano selecionado.',
            classeGrupo: 'grupo-evolucao',
            visualizacao: 'linha',
            rotuloTotal: 'reuniões',
            modoTotal: 'soma',
            linhaInteira: true,
            itens: null,
            carregando: false
        },
        {
            id: 'reunioes-modalidade',
            titulo: 'Reuniões por modalidade',
            descricao: 'Reuniões presenciais, virtuais e híbridas no ano selecionado.',
            classeGrupo: 'grupo-registro',
            visualizacao: 'rosca',
            rotuloTotal: 'reuniões',
            modoTotal: 'soma',
            itens: null,
            carregando: false
        },
        {
            id: 'reunioes-status',
            titulo: 'Reuniões por status',
            descricao: 'Reuniões agrupadas pelo status no ano selecionado.',
            classeGrupo: 'grupo-operacional',
            visualizacao: 'rosca',
            rotuloTotal: 'reuniões',
            modoTotal: 'soma',
            itens: null,
            carregando: false
        },
        {
            id: 'tarefas-situacao',
            titulo: 'Tarefas por situação',
            descricao: 'Tarefas das comissões em aberto e concluídas.',
            classeGrupo: 'grupo-qualidade',
            visualizacao: 'rosca',
            rotuloTotal: 'tarefas',
            modoTotal: 'soma',
            itens: null,
            carregando: false
        }
    ];

    cardDetalhe: CctCard | null = null;
    grupoCodigoDetalhe: string | null = null;
    linhasDetalhe: PendenciaLinha[] = [];
    totalDetalhe = 0;
    firstDetalhe = 0;
    rowsDetalhe = 100;
    carregandoDetalhe = false;

    private ufTokenRestrita: string | null = null;
    private requestSeq = 0;
    private readonly atualizacao$ = new Subject<void>();
    private readonly detalheChange$ = new Subject<void>();

    constructor(
        private cctService: CctService,
        private authService: AuthService,
        private destroyRef: DestroyRef,
        private ufSelecaoGestao: UfSelecaoGestaoService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.configurarUfPorToken();
        this.atualizar();
    }

    onUfSelecionadaChange(uf?: string | null): void {
        this.ufSelecionada = this.normalizarUf(uf);
        this.ufSelecaoGestao.definir(this.ufSelecionada);
        this.atualizar();
    }

    aplicar(): void {
        this.anoSelecionado = this.normalizarAno(this.anoSelecionado);
        this.atualizar();
    }

    obterTotalCard(card: CctCard): number | null {
        if (card.itens === null) {
            return null;
        }

        if (card.modoTotal === 'primeiro') {
            return card.itens[0]?.total ?? 0;
        }

        return card.itens.reduce((total, item) => total + (item.total ?? 0), 0);
    }

    obterDescricaoItem(item: MedicoEstatisticaAgrupadaResponse): string {
        return item.descricao?.trim() || item.codigo?.trim() || 'Não informado';
    }

    obterLarguraBarra(card: CctCard, item: MedicoEstatisticaAgrupadaResponse): string {
        const total = item.total ?? 0;
        const maiorTotal = Math.max(...(card.itens ?? []).map((i) => i.total ?? 0), 0);
        if (!total || !maiorTotal) {
            return '0%';
        }
        return `${Math.max(6, Math.round((total / maiorTotal) * 100))}%`;
    }

    obterTipoGrafico(card: CctCard): 'line' | 'bar' | 'doughnut' {
        if (card.visualizacao === 'linha') return 'line';
        if (card.visualizacao === 'rosca') return 'doughnut';
        return 'bar';
    }

    obterDadosGrafico(card: CctCard): any {
        if (!card.itens?.length) {
            return null;
        }

        const labels = card.itens.map((item) => this.obterDescricaoItem(item));
        const data = card.itens.map((item) => item.total ?? 0);
        const corBase = this.obterCor();

        if (card.visualizacao === 'linha') {
            return { labels, datasets: [{ label: card.titulo, data, fill: false, borderColor: corBase, tension: 0.35 }] };
        }

        if (card.visualizacao === 'rosca') {
            const cores = this.gerarCores(corBase, data.length);
            return { labels, datasets: [{ data, backgroundColor: cores, hoverBackgroundColor: cores, borderWidth: 1 }] };
        }

        return { labels, datasets: [{ label: 'Total', data, backgroundColor: corBase, borderRadius: 4 }] };
    }

    obterOpcoesGrafico(card: CctCard): any {
        const isDark = document.documentElement.classList.contains('dark') || document.documentElement.classList.contains('app-dark');
        const textColor = isDark ? '#f8fafc' : '#334155';
        const gridColor = isDark ? '#334155' : '#e2e8f0';

        if (card.visualizacao === 'rosca') {
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom', labels: { color: textColor, boxWidth: 10, padding: 8, font: { size: 10 } } } }
            };
        }

        return {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: card.visualizacao === 'barra' ? 'y' : 'x',
            plugins: { legend: { display: false } },
            scales: {
                x: { ticks: { color: textColor, font: { size: 9 } }, grid: { color: card.visualizacao === 'barra' ? gridColor : 'transparent' } },
                y: { ticks: { color: textColor, font: { size: 9 } }, grid: { color: card.visualizacao === 'barra' ? 'transparent' : gridColor } }
            }
        };
    }

    possuiDetalhe(card: CctCard): boolean {
        return this.obterOpcoesGrupo(card).length > 0;
    }

    obterOpcoesGrupo(card: CctCard | null): MedicoEstatisticaAgrupadaResponse[] {
        return (card?.itens ?? []).filter((item) => !!item.codigo && (item.total ?? 0) > 0);
    }

    verDetalhe(card: CctCard): void {
        const primeira = this.obterOpcoesGrupo(card)[0];
        if (!primeira?.codigo) {
            return;
        }
        this.detalheChange$.next();
        this.cardDetalhe = card;
        this.grupoCodigoDetalhe = primeira.codigo;
        this.firstDetalhe = 0;
        this.linhasDetalhe = [];
        this.totalDetalhe = 0;
        this.carregarDetalhe();
    }

    voltarParaCards(): void {
        this.detalheChange$.next();
        this.cardDetalhe = null;
        this.grupoCodigoDetalhe = null;
        this.linhasDetalhe = [];
        this.totalDetalhe = 0;
        this.firstDetalhe = 0;
        this.carregandoDetalhe = false;
    }

    onGrupoDetalheChange(codigo: string): void {
        this.detalheChange$.next();
        this.grupoCodigoDetalhe = codigo;
        this.firstDetalhe = 0;
        this.linhasDetalhe = [];
        this.totalDetalhe = 0;
        this.carregarDetalhe();
    }

    onPageChangeDetalhe(event: PaginatorState): void {
        this.detalheChange$.next();
        this.firstDetalhe = event.first ?? 0;
        this.rowsDetalhe = event.rows ?? this.rowsDetalhe;
        this.carregarDetalhe();
    }

    obterIndicadorDetalhe(): IndicadorPendencia | null {
        if (!this.cardDetalhe) {
            return null;
        }
        const grupo = this.obterOpcoesGrupo(this.cardDetalhe).find((item) => item.codigo === this.grupoCodigoDetalhe);
        return {
            id: this.cardDetalhe.id,
            titulo: this.cardDetalhe.titulo,
            descricao: grupo ? `${this.cardDetalhe.descricao} Grupo: ${this.obterDescricaoItem(grupo)}.` : this.cardDetalhe.descricao,
            grupo: 'Operacional' as IndicadorPendencia['grupo'],
            classeGrupo: 'grupo-registro',
            quantidade: this.totalDetalhe || null
        };
    }

    private carregarDetalhe(): void {
        if (!this.cardDetalhe || !this.grupoCodigoDetalhe) {
            return;
        }
        const cardId = this.cardDetalhe.id;
        const codigo = this.grupoCodigoDetalhe;
        const size = this.rowsDetalhe;
        const page = size > 0 ? Math.floor(this.firstDetalhe / size) : 0;

        this.carregandoDetalhe = true;
        this.cctService
            .consultarDetalhe(cardId, this.ufSelecionada, this.anoSelecionado, page, size, codigo)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.detalheChange$),
                // markForCheck: estado assíncrono gravado em campos não-signal sob layout OnPush não seria renderizado sem marcar o caminho como dirty.
                finalize(() => {
                    if (this.cardDetalhe?.id === cardId && this.grupoCodigoDetalhe === codigo) {
                        this.carregandoDetalhe = false;
                    }
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (resp) => {
                    if (this.cardDetalhe?.id !== cardId || this.grupoCodigoDetalhe !== codigo) {
                        return;
                    }
                    this.linhasDetalhe = (resp?.content ?? []).map((item) => this.mapLinha(item));
                    this.totalDetalhe = resp?.totalElements ?? 0;
                },
                error: () => {
                    if (this.cardDetalhe?.id !== cardId || this.grupoCodigoDetalhe !== codigo) {
                        return;
                    }
                    this.linhasDetalhe = [];
                    this.totalDetalhe = 0;
                }
            });
    }

    private mapLinha(item: CctDetalheResponse): PendenciaLinha {
        return {
            uf: item.ufSgUf ?? '',
            crm: item.identificador ?? '',
            nome: item.descricao ?? '',
            situacao: item.situacao ?? '',
            detalhe: item.complemento ?? ''
        };
    }

    private atualizar(): void {
        this.atualizacao$.next();
        this.voltarParaCards();
        this.cards = this.cards.map((card) => ({ ...card, itens: null, carregando: true }));

        const requestId = ++this.requestSeq;
        const uf = this.ufSelecionada ?? undefined;

        this.carregar('comissoes-situacao', () => this.cctService.consultarComissoesPorSituacao(uf), requestId);
        this.carregar('comissoes-tipo', () => this.cctService.consultarComissoesPorTipo(uf, this.limiteRanking), requestId);
        this.carregar('comissoes-uf', () => this.cctService.consultarComissoesPorUf(uf, this.limiteRanking), requestId);
        this.carregar('reunioes-mes', () => this.cctService.consultarReunioesPorMes(uf, this.anoSelecionado), requestId);
        this.carregar('reunioes-modalidade', () => this.cctService.consultarReunioesPorModalidade(uf, this.anoSelecionado), requestId);
        this.carregar('reunioes-status', () => this.cctService.consultarReunioesPorStatus(uf, this.anoSelecionado, this.limiteRanking), requestId);
        this.carregar('tarefas-situacao', () => this.cctService.consultarTarefasPorSituacao(uf), requestId);
    }

    private carregar(id: CctCardId, consulta: () => Observable<MedicoEstatisticaAgrupadaResponse[]>, requestId: number): void {
        consulta()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.atualizacao$),
                finalize(() => {
                    this.definirCarregando(id, false, requestId);
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (itens) => this.atualizarCard(id, this.normalizarItens(id, itens), requestId),
                error: () => this.atualizarCard(id, [], requestId)
            });
    }

    private normalizarItens(id: CctCardId, itens: MedicoEstatisticaAgrupadaResponse[] | null): MedicoEstatisticaAgrupadaResponse[] {
        const normalizados = (itens ?? []).map((item) => ({
            ...item,
            codigo: item.codigo?.trim() || null,
            descricao: item.descricao?.trim() || item.codigo?.trim() || 'Não informado',
            total: item.total ?? 0
        }));

        return CCT_CARDS_MENSAIS.has(id) ? EstatisticaMensalUtils.preencherMesesAusentes(normalizados) : normalizados;
    }

    private atualizarCard(id: CctCardId, itens: MedicoEstatisticaAgrupadaResponse[], requestId: number): void {
        if (requestId !== this.requestSeq) {
            return;
        }
        this.cards = this.cards.map((card) => (card.id === id ? { ...card, itens } : card));
    }

    private definirCarregando(id: CctCardId, carregando: boolean, requestId: number): void {
        if (requestId !== this.requestSeq) {
            return;
        }
        this.cards = this.cards.map((card) => (card.id === id ? { ...card, carregando } : card));
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        if (/^[A-Z]{2}$/.test(ufToken) && ufToken !== 'BR') {
            this.ufTokenRestrita = ufToken;
            this.ufsPermitidas = [ufToken];
            this.ufSelecionada = ufToken;
            this.ufSelecaoGestao.definir(ufToken);
        } else {
            this.ufTokenRestrita = null;
            this.ufsPermitidas = null;
            this.ufSelecionada = this.ufSelecaoGestao.uf();
        }
    }

    private normalizarUf(uf?: string | null): string | null {
        const v = String(uf ?? '')
            .trim()
            .toUpperCase();
        if (!v || v === 'BR' || !/^[A-Z]{2}$/.test(v)) return this.ufTokenRestrita;
        if (this.ufTokenRestrita && v !== this.ufTokenRestrita) return this.ufTokenRestrita;
        return v;
    }

    private normalizarAno(valor: number): number {
        const ano = Number(valor);
        if (!Number.isFinite(ano)) return this.anoAtual;
        return Math.min(Math.max(Math.trunc(ano), 1900), this.anoAtual + 1);
    }

    private obterCor(): string {
        return getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || '#3b82f6';
    }

    private gerarCores(hexCor: string, totalItens: number): string[] {
        const corPadrao = /^#[0-9a-f]{6}$/i.test(hexCor) ? hexCor : '#3b82f6';
        const r = parseInt(corPadrao.slice(1, 3), 16);
        const g = parseInt(corPadrao.slice(3, 5), 16);
        const b = parseInt(corPadrao.slice(5, 7), 16);
        return Array.from({ length: totalItens }, (_, index) => `rgba(${r}, ${g}, ${b}, ${Math.max(0.24, 1 - index * 0.14)})`);
    }
}
